import client from './client'
import type { Race } from '@/types/events'
import type {
  WizardForm,
  WizardPage,
  WizardField,
  StoreWizardFormPayload,
  UpdateWizardFormPayload,
  StoreWizardPagePayload,
  UpdateWizardPagePayload,
  ReorderWizardPagesPayload,
  StoreWizardFieldPayload,
  UpdateWizardFieldPayload,
  AttachWizardFormPayload,
} from '@/types/wizard-forms'

const BASE = '/organizer/wizard-forms'
const RACES_BASE = '/organizer/races'

// Wizard Forms

export async function listWizardForms(): Promise<WizardForm[]> {
  const { data } = await client.get<{ data: WizardForm[] }>(BASE)
  return data.data
}

export async function createWizardForm(payload: StoreWizardFormPayload): Promise<WizardForm> {
  const { data } = await client.post<{ data: WizardForm }>(BASE, payload)
  return data.data
}

export async function getWizardForm(formId: number): Promise<WizardForm> {
  const { data } = await client.get<{ data: WizardForm }>(`${BASE}/${formId}`)
  return data.data
}

export async function updateWizardForm(
  formId: number,
  payload: UpdateWizardFormPayload,
): Promise<WizardForm> {
  const { data } = await client.put<{ data: WizardForm }>(`${BASE}/${formId}`, payload)
  return data.data
}

export async function deleteWizardForm(formId: number): Promise<void> {
  await client.delete(`${BASE}/${formId}`)
}

// Wizard Pages

export async function listWizardPages(formId: number): Promise<WizardPage[]> {
  const { data } = await client.get<{ data: WizardPage[] }>(`${BASE}/${formId}/pages`)
  return data.data
}

export async function createWizardPage(
  formId: number,
  payload: StoreWizardPagePayload,
): Promise<WizardPage> {
  const { data } = await client.post<{ data: WizardPage }>(`${BASE}/${formId}/pages`, payload)
  return data.data
}

export async function getWizardPage(formId: number, pageId: number): Promise<WizardPage> {
  const { data } = await client.get<{ data: WizardPage }>(`${BASE}/${formId}/pages/${pageId}`)
  return data.data
}

export async function updateWizardPage(
  formId: number,
  pageId: number,
  payload: UpdateWizardPagePayload,
): Promise<WizardPage> {
  const { data } = await client.put<{ data: WizardPage }>(
    `${BASE}/${formId}/pages/${pageId}`,
    payload,
  )
  return data.data
}

export async function deleteWizardPage(formId: number, pageId: number): Promise<void> {
  await client.delete(`${BASE}/${formId}/pages/${pageId}`)
}

export async function reorderWizardPages(
  formId: number,
  payload: ReorderWizardPagesPayload,
): Promise<WizardPage[]> {
  const { data } = await client.put<{ data: WizardPage[] }>(
    `${BASE}/${formId}/pages/reorder`,
    payload,
  )
  return data.data
}

// Wizard Fields

export async function listWizardFields(formId: number, pageId: number): Promise<WizardField[]> {
  const { data } = await client.get<{ data: WizardField[] }>(
    `${BASE}/${formId}/pages/${pageId}/fields`,
  )
  return data.data
}

export async function createWizardField(
  formId: number,
  pageId: number,
  payload: StoreWizardFieldPayload,
): Promise<WizardField> {
  const { data } = await client.post<{ data: WizardField }>(
    `${BASE}/${formId}/pages/${pageId}/fields`,
    payload,
  )
  return data.data
}

export async function getWizardField(
  formId: number,
  pageId: number,
  fieldId: number,
): Promise<WizardField> {
  const { data } = await client.get<{ data: WizardField }>(
    `${BASE}/${formId}/pages/${pageId}/fields/${fieldId}`,
  )
  return data.data
}

export async function updateWizardField(
  formId: number,
  pageId: number,
  fieldId: number,
  payload: UpdateWizardFieldPayload,
): Promise<WizardField> {
  const { data } = await client.put<{ data: WizardField }>(
    `${BASE}/${formId}/pages/${pageId}/fields/${fieldId}`,
    payload,
  )
  return data.data
}

export async function deleteWizardField(
  formId: number,
  pageId: number,
  fieldId: number,
): Promise<void> {
  await client.delete(`${BASE}/${formId}/pages/${pageId}/fields/${fieldId}`)
}

// Race Form Attachment

export async function attachWizardFormToRace(
  raceId: number,
  payload: AttachWizardFormPayload,
): Promise<Race> {
  const { data } = await client.post<{ data: Race }>(
    `${RACES_BASE}/${raceId}/attach-form`,
    payload,
  )
  return data.data
}

export async function detachWizardFormFromRace(raceId: number): Promise<void> {
  await client.delete(`${RACES_BASE}/${raceId}/detach-form`)
}
